import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Sparkles, Loader, Send, RefreshCw, Trash2 } from 'lucide-react';
import { aiInsightsService } from '../services/aiInsightsService';

/**
 * AI Insights modal for Call Report
 * Uses currently filtered call_reports + joined service_requests rows passed in via props.
 */
const AISummaryModal = ({ open, onClose, filters = null }) => {
  const [question, setQuestion] = useState('');
  const [chat, setChat] = useState([
    {
      role: 'assistant',
      content:
        'Haan boliye — call logs/complaints ke data se aap jo poochoge uske basis par insights dunga. Aap kya dekhna chahte ho?',
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [datasetId, setDatasetId] = useState('');
  const chatEndRef = useRef(null);
  const snapshotKey = useMemo(() => {
    return JSON.stringify({
      df: filters?.dateFilter || null,
      sf: filters?.statusFilter || null,
      cf: filters?.callTypeFilter || null,
      agent: filters?.agentCallReportId || null,
      scoped: Boolean(filters?.scopeToCurrentUser),
    });
  }, [filters]);

  const suggestedQuestions = [
    'Aaj ke calls me sabse zyada issue kya hai?',
    'Top cancellation reasons kya hain?',
    'Which city me sabse zyada calls aaye?',
    'Which service_type ka volume highest hai + avg duration?',
    'Linked complaints me pending/assigned ka kya status hai?',
  ];

  useEffect(() => {
    if (!open) return;
    // scroll to bottom on open
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, [open]);

  // Create/refresh dataset on open or filter change
  useEffect(() => {
    if (!open) return;
    const run = async () => {
      setError('');
      setDatasetId('');
      const res = await aiInsightsService.createCallReportDataset(
        { dateFilter: filters?.dateFilter, statusFilter: filters?.statusFilter, callTypeFilter: filters?.callTypeFilter },
        { agentCallReportId: filters?.scopeToCurrentUser ? filters?.agentCallReportId : null },
        snapshotKey
      );
      if (!res.success) {
        setError(res.error || 'Failed to prepare dataset');
        return;
      }
      setDatasetId(res.datasetId || '');
    };
    run();
  }, [open, snapshotKey, filters]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat]);

  const send = async (q) => {
    const finalQ = (q ?? question).trim();
    if (!finalQ) return;

    setIsLoading(true);
    setError('');
    setQuestion('');

    const nextMessages = [...chat, { role: 'user', content: finalQ }];
    setChat(nextMessages);
    try {
      if (!datasetId) throw new Error('Dataset not ready yet. Please wait a moment and try again.');
      const res = await aiInsightsService.chatCallReportInsights(nextMessages.filter((m) => m.role !== 'system'), datasetId);
      if (!res.success) throw new Error(res.error || 'Failed');
      setChat((prev) => [...prev, { role: 'assistant', content: res.answer || '' }]);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-indigo-600 text-white px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles size={18} className="flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base md:text-xl font-bold truncate">AI Chat (Call Insights)</h2>
              <p className="text-xs md:text-sm text-white/80 truncate">
                Uses current filters + call_reports + service_requests
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                setChat([
                  {
                    role: 'assistant',
                    content:
                      'Haan boliye — call logs/complaints ke data se aap jo poochoge uske basis par insights dunga. Aap kya dekhna chahte ho?',
                  },
                ])
              }
              className="p-2 rounded-lg hover:bg-white/15 transition-colors"
              title="Clear chat"
              disabled={isLoading}
            >
              <Trash2 size={18} />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/15 transition-colors"
              disabled={isLoading}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 md:p-6 overflow-y-auto space-y-4">
          {/* Suggested */}
          <div className="flex flex-wrap gap-2">
            {suggestedQuestions.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => {
                  setQuestion(q);
                  send(q);
                }}
                className="text-xs px-3 py-1.5 rounded-full border border-gray-200 hover:border-teal-300 hover:bg-teal-50 text-gray-700"
                disabled={isLoading}
                title="Ask this"
              >
                {q}
              </button>
            ))}
          </div>

          {/* Chat */}
          <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
            <div className="p-3 md:p-4 space-y-3 max-h-[45vh] overflow-y-auto bg-gradient-to-b from-white to-gray-50">
              {chat.map((m, idx) => (
                <div
                  key={idx}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[90%] md:max-w-[75%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
                      m.role === 'user'
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-800 border border-gray-200'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
          </div>

          {/* Input */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ask anything about these call logs…"
                className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') send();
                }}
              />
              <button
                onClick={() => send()}
                disabled={isLoading || !question.trim()}
                className="px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                <span>{isLoading ? 'Thinking…' : 'Ask'}</span>
              </button>
            </div>
            <div className="text-xs text-gray-500 mt-2 flex items-center gap-2">
              <RefreshCw size={12} />
              <span>
              Dataset: <b>{datasetId ? 'Ready' : 'Loading…'}</b>
              </span>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Answer is shown in chat history above */}
        </div>
      </div>
    </div>
  );
};

export default AISummaryModal;


